import { GraphQLResult } from "@aws-amplify/api";
import { API, graphqlOperation } from "aws-amplify";
import * as formik from "formik";
import { useField } from "formik";
import { useState } from "react";
import { Button, Form } from "react-bootstrap";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import * as mutations from "../../graphql/mutations";
import { FormRadio, radioDataType } from "../FormRadio/FormRadio";
import { Notification } from "../Notification/Notification";
import { ToastContent } from "../Notification/types";
import {
  CreatePushNotifcationResponse,
  PushNotificationType,
} from "../_shared/pushNotifications/types";
import "./NewPushNotification.css";
import { convertDateStrings, notificationSchema } from "./utils";

export const NewPushNotifiction = () => {
  const [notification, setNotification] = useState<PushNotificationType>({
    id: uuidv4(),
    name: "",
    title: "",
    body: "",
    deeplink: "",
    dueOn: new Date().toISOString(),
    throttleRate: 30,
    autoPauseSchedule: false,
    pauseSendingTime: "",
    resumeSendingTime: "",
    application: "TRAVELAPP",
    tmcUsers: "",
    tiUsers: "",
    deviceOS: "",
    status: "SCHEDULED",
  });

  const { Formik } = formik;

  const [tmcUsersInfo, setTmcUsersInfo] = useState("");
  const [tiUsersInfo, setTiUsersInfo] = useState("");
  const [deviceOSInfo, setDeviceOSInfo] = useState(
    "android" || "ios" || "both"
  );

  const [toast, setToast] = useState<ToastContent>({
    title: "",
    description: "",
    isVisible: false,
    notificationVariant: "success",
  });

  const [disableSubmitBtn, setDisableSubmitBtn] = useState<boolean>(false);

  const timeout: number = 4000;
  const navigate = useNavigate();

  const DatePickerField = (props: formik.FieldHookConfig<DatePicker>) => {
    const [field, meta, helpers] = useField(props);
    return (
      <DatePicker
        selected={field.value && new Date(field.value)}
        onChange={(value) => helpers.setValue(value)}
        showMonthPicker
        showTimeInput
        showMonthDropdown
        showYearDropdown
        timeInputLabel="Time:"
        dateFormat="dd/MM/yyyy h:mm aa"
        className="datePicker"
        name="dueOn"
      />
    );
  };

  const handleSubmitNotification = async (values: PushNotificationType) => {
    const time = convertDateStrings(values.dueOn);
    const pushNotification: PushNotificationType = {
      ...values,
      id: uuidv4(),
      scheduleId: undefined,
      dueOn: time,
      tiUsers: tiUsersInfo,
      tmcUsers: tmcUsersInfo,
      deviceOS: deviceOSInfo,
    };
    try {
      const response = (await API.graphql(
        graphqlOperation(mutations.createPushNotification, {
          pushNotification: pushNotification,
        })
      )) as GraphQLResult<CreatePushNotifcationResponse>;
      if (response.data.createPushNotification.scheduleResponse.scheduleId) {
        setToast({
          title: "Success",
          description: `Notification created. ${response.data.createPushNotification.scheduleResponse.message}`,
          isVisible: true,
          notificationVariant: "success",
        });
        setTimeout(() => {
          navigate(-1);
        }, timeout);
      } else {
        setToast({
          title: "Error!",
          description: `Failed to create notification. ${response.data.createPushNotification.scheduleResponse.message}`,
          isVisible: true,
          notificationVariant: "danger",
        });
        throw new Error("Something went wrong");
      }
    } catch (e) {
      console.log("error is " + JSON.stringify(e));
      throw new Error("Something went wrong");
    }
  };
  const goBack = () => {
    navigate(-1);
  };
  const handleNotificationClose = () => {
    setToast({ ...toast, isVisible: false });
  };

  const radioTMC: radioDataType[] = [
    { radioLabel: "Yes", radioId: "yes", radioName: "tmcUsers" },
    { radioLabel: "N/A", radioId: "n/a", radioName: "tmcUsers" },
    { radioLabel: "No", radioId: "no", radioName: "tmcUsers" },
  ];

  const radioTI: radioDataType[] = [
    { radioLabel: "Yes", radioId: "yes", radioName: "tiUsers" },
    { radioLabel: "N/A", radioId: "n/a", radioName: "tiUsers" },
    { radioLabel: "No", radioId: "no", radioName: "tiUsers" },
  ];
  const radioDevice: radioDataType[] = [
    { radioLabel: "Both", radioId: "both", radioName: "deviceOS" },
    { radioLabel: "IOS Only", radioId: "ios", radioName: "deviceOS" },
    {
      radioLabel: "Android Only",
      radioId: "android",
      radioName: "deviceOS",
    },
  ];

  return (
    <div className="screenContainer">
      <h1 className="title">New Notification</h1>
      <Formik
        validationSchema={notificationSchema}
        initialValues={notification}
        onSubmit={async (values: PushNotificationType, { setSubmitting }) => {
          setSubmitting(true);
          await handleSubmitNotification(values)
            .then(() => {
              setDisableSubmitBtn(true);
            })
            .catch(() => {
              setSubmitting(false);
            });
        }}
      >
        {({
          handleChange,
          handleSubmit,
          values,
          touched,
          errors,
          handleBlur,
          isSubmitting,
        }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <div className="formContainer">
              <div className="formItem">
                <Form.Group as={Row}>
                  <Form.Label column sm={3}>
                    Notification name (Internal reference)
                  </Form.Label>
                  <Col sm={9}>
                    <Form.Control
                      name="name"
                      aria-label="Notificaton Name"
                      size="lg"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.name}
                      isValid={touched.name && !errors.name}
                      isInvalid={!!errors.name}
                    ></Form.Control>
                    <Form.Control.Feedback type="invalid">
                      {errors.name}
                    </Form.Control.Feedback>
                  </Col>
                </Form.Group>
              </div>
              <div className="formItem">
                <Form.Group as={Row}>
                  <Form.Label column sm={3}>
                    Notification title
                  </Form.Label>
                  <Col sm={9}>
                    <Form.Control
                      name="title"
                      aria-label="Notificaton Title"
                      size="lg"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.title}
                      isValid={touched.title && !errors.title}
                      isInvalid={!!errors.title}
                    ></Form.Control>
                    <Form.Control.Feedback type="invalid">
                      {errors.title}
                    </Form.Control.Feedback>
                  </Col>
                </Form.Group>
              </div>
              <div className="formItem">
                <Form.Group as={Row}>
                  <Form.Label column sm={3}>
                    Notification body
                  </Form.Label>
                  <Col sm={9}>
                    <Form.Control
                      name="body"
                      aria-label="Notificaton Body"
                      size="lg"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.body}
                      isValid={touched.body && !errors.body}
                      isInvalid={!!errors.body}
                      as={"textarea"}
                    ></Form.Control>
                    <Form.Control.Feedback type="invalid">
                      {errors.body}
                    </Form.Control.Feedback>
                  </Col>
                </Form.Group>
              </div>
              <div className="formItem">
                <Form.Group as={Row}>
                  <Form.Label column sm={3}>
                    Notification deeplink
                  </Form.Label>
                  <Col sm={9}>
                    <Form.Control
                      name="deeplink"
                      aria-label="Deeplink"
                      size="lg"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.deeplink}
                      isValid={touched.deeplink && !errors.deeplink}
                      isInvalid={!!errors.deeplink}
                    ></Form.Control>
                    <Form.Control.Feedback type="invalid">
                      {errors.deeplink}
                    </Form.Control.Feedback>
                  </Col>
                </Form.Group>
              </div>
              <Row
                as={"div"}
                className="formItem"
                style={{ height: 48, alignItems: "center" }}
              >
                <Col xs={3}>
                  <span>Due on (time is rounded)</span>
                </Col>

                <Col xs={9}>
                  <DatePickerField name="dueOn" />
                </Col>
              </Row>
              <div className="formItem">
                <Form.Group as={Row}>
                  <Form.Label column sm={3}>
                    Throttle rate (number of notifications sent per second)
                  </Form.Label>
                  <Col sm={9} style={{ margin: "auto" }}>
                    <Form.Control
                      name="throttleRate"
                      aria-label="Throttle rate"
                      size="lg"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.throttleRate}
                      isValid={touched.throttleRate && !errors.throttleRate}
                      isInvalid={!!errors.throttleRate}
                    ></Form.Control>
                    <Form.Control.Feedback type="invalid">
                      {errors.throttleRate}
                    </Form.Control.Feedback>
                  </Col>
                </Form.Group>
              </div>
              <div className="formItem">
                <Form.Group as={Row} className="checkContainer">
                  <Form.Label column sm={3}>
                    Automatically pause schedule?
                  </Form.Label>
                  <Col sm={9} style={{ margin: "auto" }}>
                    <Form.Check
                      type="switch"
                      name="autoPauseSchedule"
                      aria-label="Automatically pause schedule"
                      onChange={handleChange}
                      className="checkedInput"
                    />
                  </Col>
                </Form.Group>
              </div>
              <div className="formItem">
                <Form.Group as={Row}>
                  <Form.Label column sm={3}>
                    Pause sending time
                  </Form.Label>
                  <Col sm={9} style={{ margin: "auto" }}>
                    <Form.Control
                      name="pauseSendingTime"
                      aria-label="Pause sending time"
                      size="lg"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.pauseSendingTime}
                      isValid={
                        touched.pauseSendingTime && !errors.pauseSendingTime
                      }
                      isInvalid={!!errors.pauseSendingTime}
                    ></Form.Control>
                    <Form.Control.Feedback type="invalid">
                      {errors.pauseSendingTime}
                    </Form.Control.Feedback>
                  </Col>
                </Form.Group>
              </div>
              <div className="formItem">
                <Form.Group as={Row}>
                  <Form.Label column sm={3}>
                    Resume sending time
                  </Form.Label>
                  <Col sm={9} style={{ margin: "auto" }}>
                    <Form.Control
                      name="resumeSendingTime"
                      aria-label="Resume sending time"
                      size="lg"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.resumeSendingTime}
                      isValid={
                        touched.resumeSendingTime && !errors.resumeSendingTime
                      }
                      isInvalid={!!errors.resumeSendingTime}
                    ></Form.Control>
                    <Form.Control.Feedback type="invalid">
                      {errors.resumeSendingTime}
                    </Form.Control.Feedback>
                  </Col>
                </Form.Group>
              </div>
              <div className="formItem">
                <Form.Group as={Row}>
                  <Form.Label column sm={3}>
                    Application
                  </Form.Label>
                  <Col sm={9} style={{ margin: "auto" }}>
                    <Form.Control
                      name="application"
                      aria-label="application"
                      size="lg"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.application}
                      isValid={touched.application && !errors.application}
                      isInvalid={!!errors.application}
                      disabled
                    ></Form.Control>
                    <Form.Control.Feedback type="invalid">
                      {errors.application}
                    </Form.Control.Feedback>
                  </Col>
                </Form.Group>
              </div>
              <div className="formItem">
                <FormRadio
                  radioData={radioTMC}
                  labelText="TMC users"
                  setInfo={setTmcUsersInfo}
                  defaultValue="n/a"
                ></FormRadio>
              </div>
              <div className="formItem">
                <FormRadio
                  radioData={radioTI}
                  labelText="TI users"
                  setInfo={setTiUsersInfo}
                  defaultValue="n/a"
                ></FormRadio>
              </div>
              <div className="formItem">
                <FormRadio
                  radioData={radioDevice}
                  labelText="Device OS"
                  setInfo={setDeviceOSInfo}
                  defaultValue="both"
                ></FormRadio>
              </div>
              <div className="formItem">
                <Button
                  disabled={isSubmitting || disableSubmitBtn}
                  type="submit"
                  className="btn btn-danger save-button"
                >
                  Save
                </Button>
                <Button
                  type="button"
                  className="btn btn-secondary"
                  onClick={goBack}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
      <Notification
        show={toast.isVisible}
        timer={timeout}
        position="bottom-end"
        title={toast.title}
        description={toast.description}
        autohide={true}
        handleClose={handleNotificationClose}
        variant={toast.notificationVariant}
      />
    </div>
  );
};
